// Invoice and payment settings, the printed invoice, payroll.
import { addDays, type Cents, formatMoney, SETTINGS_DEFAULTS, type Settings as SettingsShape, weekOf } from "@steward/shared";
import { useState } from "react";
import { useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Choice, InputText } from "../../components/fields";
import { shortDate, todayIn } from "../../components/format";
import { Modal, useToast } from "../../components/overlays";
import { Banner, Button, Card, Details, SettingRow, Switch } from "../../components/parts";
import { errorText } from "./common";

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function MoneySettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const [editing, setEditing] = useState<"invoices" | "print" | null>(null);
  if (!s.data) return <Card title="Invoices">Loading</Card>;
  const { settings, own } = s.data;
  const ownInv = (own.invoices ?? {}) as Record<string, unknown>;
  const from = (set: Record<string, unknown>, key: string) => (key in set ? null : <span className="muted"> · from above</span>);
  const edit = (k: "invoices" | "print") =>
    can("settings.update") ? (
      <Button type="secondary" size="small" onClick={() => setEditing(k)}>
        Edit
      </Button>
    ) : null;
  return (
    <>
      <Card id="invoices" title="Invoices and payments" action={edit("invoices")} flush>
        <Details
          rows={[
            [
              "Email subject",
              <>
                {settings.invoices.subject}
                {from(ownInv, "subject")}
              </>,
            ],
            [
              "Message",
              <>
                {settings.invoices.text}
                {from(ownInv, "text")}
              </>,
            ],
            [
              "Email words",
              <>
                {settings.invoices.email || "Built in words for your trade"}
                {from(ownInv, "email")}
              </>,
            ],
            [
              "Tips on the pay page",
              <>
                {settings.invoices.tips ? `On, ${settings.invoices.tipPercents.map((p) => `${String(p)}%`).join(", ")}` : "Off"}
                {from(ownInv, "tips")}
              </>,
            ],
            [
              "Ways to pay",
              <>
                {[
                  settings.invoices.payWays.card && "Card, Apple Pay, Google Pay",
                  settings.invoices.payWays.bank && "Bank",
                  settings.invoices.payWays.klarna &&
                    `Klarna on one-time work over ${formatMoney(settings.invoices.klarnaMinCents as Cents)}`,
                ]
                  .filter(Boolean)
                  .join(", ")}
                {from(ownInv, "payWays")}
              </>,
            ],
            [
              "Visit photos on the pay page",
              <>
                {settings.invoices.photosOnPayPage ? "Shown" : "Off"}
                {from(ownInv, "photosOnPayPage")}
              </>,
            ],
            [
              "Saved cards charged by themselves",
              <>
                {AUTO_CHARGE_WORDS[settings.invoices.autoChargeWhen]}
                {from(ownInv, "autoChargeWhen")}
              </>,
            ],
            [
              "Card saved or paid with",
              <>
                {AUTO_ON_SAVE_WORDS[settings.invoices.autoChargeOnSave]}
                {from(ownInv, "autoChargeOnSave")}
              </>,
            ],
            [
              "Homes past due after",
              <>
                {settings.invoices.pastDueHoursHome} hours{from(ownInv, "pastDueHoursHome")}
              </>,
            ],
            [
              "Businesses past due after",
              <>
                {settings.invoices.pastDueDaysBusiness} days{from(ownInv, "pastDueDaysBusiness")}
              </>,
            ],
          ]}
        />
      </Card>
      <Card id="on-invoice" title="On every invoice" action={edit("print")} flush>
        <Details
          rows={[
            [
              "Cash or check note",
              settings.invoices.payNote ? (
                <>
                  {settings.invoices.payNote}
                  {from(ownInv, "payNote")}
                </>
              ) : null,
            ],
            [
              "Small print",
              settings.invoices.footer ? (
                <>
                  {settings.invoices.footer}
                  {from(ownInv, "footer")}
                </>
              ) : null,
            ],
            [
              "Bill part of a job early",
              <>
                {settings.invoices.progress ? "On, from a one-time visit's More menu" : "Off"}
                {from(ownInv, "progress")}
              </>,
            ],
          ]}
        />
      </Card>
      <p className="muted">
        "From above" means this location uses what the brand or head office set. Changing it here sets it for this location only.
      </p>
      {editing === "invoices" ? <InvoiceEdit settings={settings} onClose={() => setEditing(null)} /> : null}
      {editing === "print" ? <PrintEdit settings={settings} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function InvoiceEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [v, setV] = useState(settings.invoices);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit invoice settings"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { invoices: v },
                {
                  onSuccess: () => {
                    toast("Invoice settings saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <InputText
        label="Email subject"
        value={v.subject}
        onChange={(e) => setV({ ...v, subject: e.target.value })}
        description="{brand} becomes your business name."
      />
      <InputText
        label="Message"
        value={v.text}
        onChange={(e) => setV({ ...v, text: e.target.value })}
        description="{first} becomes the client's first name, {job} the job's name."
      />
      <label className="field wide">
        <span className="label">Email words</span>
        <textarea
          rows={5}
          maxLength={3000}
          value={v.email}
          placeholder="Leave empty to use the built in words for your trade."
          onChange={(e) => setV({ ...v, email: e.target.value })}
        />
        <span className="description">
          Shows above the amount and the Pay invoice button. {"{first}"} becomes the client's first name, {"{job}"} the job's
          name. Your name and phone go at the bottom by themselves.
        </span>
      </label>
      <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border)" }}>
        <SettingRow
          title="Tips on the pay page"
          changed={v.tips !== SETTINGS_DEFAULTS.invoices.tips}
          description="10%, 15%, 20% or another amount. Nothing picked for them."
          control={<Switch label="Tips" checked={v.tips} onChange={(x) => setV({ ...v, tips: x })} />}
        />
        <SettingRow
          title="Visit photos on the pay page"
          changed={v.photosOnPayPage !== SETTINGS_DEFAULTS.invoices.photosOnPayPage}
          description="Proof the work was done, like a mowed yard. Leave off for cleaning."
          control={<Switch label="Photos" checked={v.photosOnPayPage} onChange={(x) => setV({ ...v, photosOnPayPage: x })} />}
        />
        <SettingRow
          title="Card, Apple Pay, Google Pay"
          description="Always on"
          control={<Switch label="Card" checked disabled onChange={() => undefined} />}
        />
        <SettingRow
          title="Bank payment"
          changed={v.payWays.bank !== SETTINGS_DEFAULTS.invoices.payWays.bank}
          control={
            <Switch label="Bank" checked={v.payWays.bank} onChange={(x) => setV({ ...v, payWays: { ...v.payWays, bank: x } })} />
          }
        />
        <SettingRow
          title="Klarna"
          changed={v.payWays.klarna !== SETTINGS_DEFAULTS.invoices.payWays.klarna}
          control={
            <Switch
              label="Klarna"
              checked={v.payWays.klarna}
              onChange={(x) => setV({ ...v, payWays: { ...v.payWays, klarna: x } })}
            />
          }
        />
      </div>
      {v.payWays.klarna ? (
        <InputText
          label="Klarna shows on one-time work over"
          type="number"
          min={35}
          prefix="$"
          value={v.klarnaMinCents / 100}
          onChange={(e) => setV({ ...v, klarnaMinCents: Math.round(Number(e.target.value) * 100) })}
          description="Klarna is pay in 4. It never shows on repeating work or for clients charged by themselves."
        />
      ) : null}
      <Choice
        label="Charge saved cards by themselves"
        value={v.autoChargeWhen}
        onChange={(x) => setV({ ...v, autoChargeWhen: x })}
        options={(["now", "hour", "evening"] as const).map((x) => ({ value: x, label: AUTO_CHARGE_WORDS[x] }))}
        description="For repeating jobs whose client agreed to auto charge. A later time gives you room to fix the price or press Hold."
      />
      <Choice
        label="When a client saves or pays with a card"
        value={v.autoChargeOnSave}
        onChange={(x) => setV({ ...v, autoChargeOnSave: x })}
        options={(["on", "ask"] as const).map((x) => ({ value: x, label: AUTO_ON_SAVE_WORDS[x] }))}
        description="Clients on a repeating job agree under the Card terms. Ask shows a box on the pay page they tick first."
      />
      {v.tips ? (
        <div className="form-grid">
          {v.tipPercents.map((p, n) => (
            <InputText
              key={n}
              label={`Tip button ${String(n + 1)}`}
              type="number"
              min={1}
              max={50}
              suffix="%"
              value={p}
              onChange={(e) => setV({ ...v, tipPercents: v.tipPercents.map((x, k) => (k === n ? Number(e.target.value) : x)) })}
            />
          ))}
        </div>
      ) : null}
      <div className="form-grid">
        <InputText
          label="Homes past due after"
          type="number"
          min={0}
          suffix="hours"
          value={v.pastDueHoursHome}
          onChange={(e) => setV({ ...v, pastDueHoursHome: Number(e.target.value) })}
        />
        <InputText
          label="Businesses past due after"
          type="number"
          min={0}
          suffix="days"
          value={v.pastDueDaysBusiness}
          onChange={(e) => setV({ ...v, pastDueDaysBusiness: Number(e.target.value) })}
        />
      </div>
    </Modal>
  );
}

/** The words on every invoice: the cash or check note, the small print, and billing part of a job early. */
function PrintEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [v, setV] = useState({
    payNote: settings.invoices.payNote,
    footer: settings.invoices.footer,
    progress: settings.invoices.progress,
  });
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit what every invoice says"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { invoices: v },
                {
                  onSuccess: () => {
                    toast("Invoice words saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <label className="field wide">
        <span className="label">Cash or check note</span>
        <textarea
          rows={3}
          maxLength={500}
          value={v.payNote}
          placeholder="Make checks out to The Cleaners. Leave cash on the kitchen counter."
          onChange={(e) => setV({ ...v, payNote: e.target.value })}
        />
        <span className="description">Shows under the pay button and on the PDF while money is owed.</span>
      </label>
      <label className="field wide">
        <span className="label">Small print</span>
        <textarea
          rows={4}
          maxLength={1500}
          value={v.footer}
          placeholder="Late fees, cancel rules or anything your lawyer wants on every invoice."
          onChange={(e) => setV({ ...v, footer: e.target.value })}
        />
        <span className="description">Shows at the bottom of the pay page and the PDF.</span>
      </label>
      <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border)" }}>
        <SettingRow
          title="Bill part of a job early"
          changed={v.progress !== SETTINGS_DEFAULTS.invoices.progress}
          description="A one-time visit's More menu gets Bill part now, for a deposit or a big job paid in steps."
          control={<Switch label="Bill part of a job early" checked={v.progress} onChange={(x) => setV({ ...v, progress: x })} />}
        />
      </div>
    </Modal>
  );
}

const AUTO_CHARGE_WORDS = { now: "Right after the visit", hour: "An hour after the visit", evening: "At 6 PM that day" } as const;

const AUTO_ON_SAVE_WORDS = { on: "Charge after each visit", ask: "Ask them first" } as const;

export function PayrollEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const { company } = useSession();
  const [day, setDay] = useState(String(settings.payroll.paydayWeekday));
  const [pool, setPool] = useState(
    settings.payroll.defaultPoolPercent === null ? "" : String(settings.payroll.defaultPoolPercent),
  );
  const [business, setBusiness] = useState(
    settings.payroll.commercialPoolPercent === null ? "" : String(settings.payroll.commercialPoolPercent),
  );
  const [every, setEvery] = useState(String(settings.payroll.periodWeeks));
  // Two-week pay starts on a Monday after the last paid week; offer this Monday and the next five.
  const monday = weekOf(todayIn(company.timezone));
  const mondays = [...new Set([settings.payroll.twoWeekFrom, ...[0, 1, 2, 3, 4, 5].map((n) => addDays(monday, n * 7))])]
    .filter((d): d is string => d !== null)
    .sort();
  const [firstMonday, setFirstMonday] = useState(settings.payroll.twoWeekFrom ?? monday);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit payroll settings"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  payroll: {
                    paydayWeekday: Number(day),
                    defaultPoolPercent: pool.trim() === "" ? null : Number(pool),
                    commercialPoolPercent: business.trim() === "" ? null : Number(business),
                    ...(every === "2" ? { periodWeeks: 2 as const, twoWeekFrom: firstMonday } : { periodWeeks: 1 as const }),
                  },
                },
                {
                  onSuccess: () => {
                    toast("Payroll settings saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <Choice
        label="Pay every"
        value={every}
        onChange={setEvery}
        options={[
          { value: "1", label: "Week" },
          { value: "2", label: "Two weeks" },
        ]}
        description="Weekly is the usual. Paid weeks never change, so two-week pay starts after the last paid week."
      />
      {every === "2" ? (
        <Choice
          label="First two weeks start on"
          value={firstMonday}
          onChange={setFirstMonday}
          options={mondays.map((d) => ({ value: d, label: `Monday, ${shortDate(d)}` }))}
          description="Weeks before this day stay weekly."
        />
      ) : null}
      <Choice
        label="Payday"
        value={day}
        onChange={setDay}
        options={WEEKDAYS.slice(0, 5).map((d, i) => ({ value: String(i + 1), label: d }))}
        description="Each pay period is paid on this day after it ends."
      />
      <InputText
        label="Default pool"
        type="number"
        min={0}
        max={100}
        suffix="%"
        value={pool}
        onChange={(e) => setPool(e.target.value)}
        description="Used when nobody on a visit has a higher percent. Leave empty for none."
      />
      <InputText
        label="Business jobs pool"
        type="number"
        min={0}
        max={100}
        suffix="%"
        value={business}
        onChange={(e) => setBusiness(e.target.value)}
        description="Jobs for a business client pay this pool, whatever the crew's own percent. Leave empty to pay them like any job."
      />
    </Modal>
  );
}
